import { Grid, Paper, Alert, Typography, Button, Box } from '@mui/material';
import { FC } from 'react';
import { PolicyOverview, PolicyStatus, Product } from '@acorn-portal/shared/src/services/api';
import { format } from 'date-fns';
import { DISPLAY_DATE_TIME } from '@acorn-portal/shared/src/utils/constants';
import PolicyOverviewStatusField from '@acorn-portal/shared/src/components/status/policyOverviewStatusField';
import { getVehicleIconByProduct } from '@acorn-portal/shared/src/utils/constants/vehicleIcons';

interface IPolicyListItemProps {
	policy: PolicyOverview;
	onSelect: (policyToken: string, policyNumber: string, product: Product) => void;
	onSubscription: (policyToken: string, policyNumber: string) => void;
	onRenew: (
		policyToken: string,
		policyNumber: string,
		isShortTermPolicy: boolean,
		isMTAForSchemeDisallowed: boolean,
	) => void;
}

const PolicyListItem: FC<IPolicyListItemProps> = ({ policy, onSelect, onRenew, onSubscription }) => {
	return (
		<Paper
			className='policy-list-item'
			onClick={() => onSelect(policy.policyToken, policy.policyNumber, policy.product)}
			sx={(theme) => ({
				marginTop: '2rem',
				padding: '20px',
				borderRadius: '8px',
				backgroundColor: '#fefefefe',
				cursor: 'pointer',
				transition: '0.4s',
				border: `1px solid ${theme.palette.divider}`,
				'&:hover': {
					backgroundColor: 'rgba(78, 85, 162, 0.12)',
				},
			})}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					flexDirection: { xs: 'column', sm: 'row' },
					width: { xs: '100%', sm: 'auto' },
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						width: { xs: '100%', sm: '20em' },
						margin: '0',
						svg: {
							margin: { xs: '0 auto 2em', sm: '0 auto', width: '120px' },
						},
					}}
				>
					{getVehicleIconByProduct(policy.product, 'large')}
				</Box>
				<Box>
					<PolicyOverviewStatusField policy={policy} />
					<Typography
						variant='h5'
						component='h5'
						sx={(theme) => ({
							color: theme.palette.regNumberColor.main,
							fontSize: '1.6rem',
						})}
					>
						{policy.vehicleRegistrationNumber.toLocaleUpperCase()}
					</Typography>
					<Typography marginTop={1} variant='body2' component='p' sx={{ fontSize: '1.1rem' }}>
						{policy.vehicleMakeModel}
					</Typography>
					{policy.policyStatus === PolicyStatus.Expired ||
					policy.policyStatus === PolicyStatus.Cancelled ||
					policy.policyStatus === PolicyStatus.Quote ? null : (
						<Typography marginTop={1} variant='body2' component='p' sx={{ fontSize: '1.1rem' }}>
							Days of cover remaining: {policy.remainingDaysOfCover}
						</Typography>
					)}
					<Typography marginTop={1} variant='body2' component='p' sx={{ fontSize: '1.1rem' }}>
						Policy Number: {policy.policyNumber}
					</Typography>
					<Typography marginTop={1} variant='body2' component='p' sx={{ fontSize: '1.1rem' }}>
						{format(new Date(policy.startDate), DISPLAY_DATE_TIME)} -{' '}
						{format(new Date(policy.expiryDate), DISPLAY_DATE_TIME)}
					</Typography>
				</Box>
			</Box>

			<Grid container>
				<Grid size={{ xs: 12 }}>
					{policy.policyStatus === PolicyStatus.DueForRenewal && (
						<Alert
							variant='standard'
							severity='info'
							sx={{
								width: '100%',
								borderRadius: 2,
								marginY: { xs: 2 },
								marginBottom: { md: 0 },
								textAlign: 'left',
								alignItems: 'center',
							}}
						>
							<Button
								variant='text'
								onClick={(e) => {
									e.stopPropagation();
									onRenew(
										policy.policyToken,
										policy.policyNumber,
										policy.isShortTermPolicy,
										!!policy.isMTAForSchemeDisallowed,
									);
								}}
								sx={{
									width: 'auto',
									color: 'info.main',
									textTransform: 'unset',
									':hover': {
										background: 'transparent',
										color: 'info.light',
										textDecoration: 'underline',
									},
								}}
							>
								<Typography sx={{ textAlign: { xs: 'left', sm: 'left', md: 'center' } }}>
									Your policy is due for renewal on {format(new Date(policy.renewalDate ?? ''), DISPLAY_DATE_TIME)}{' '}
									Click Here to renew now
								</Typography>
							</Button>
						</Alert>
					)}
				</Grid>
				<Grid size={{ xs: 12 }}>
					{policy.policyStatus === PolicyStatus.EndOfProduct && (
						<Alert
							variant='standard'
							severity='info'
							sx={{
								width: '100%',
								borderRadius: 2,
								marginY: { xs: 2 },
								marginBottom: { md: 0 },
								textAlign: 'left',
								alignItems: 'center',
							}}
						>
							<Typography
								sx={{
									textAlign: { xs: 'center', sm: 'center', md: 'center' },
								}}
							>
								Your policy is due to expire on {format(new Date(policy.expiryDate), DISPLAY_DATE_TIME)}
							</Typography>
						</Alert>
					)}
				</Grid>
				<Grid size={{ xs: 12 }}>
					{policy.isSubscriptionRestartAvailable && (
						<Alert
							variant='standard'
							severity='info'
							sx={{
								width: '100%',
								borderRadius: 2,
								marginY: { xs: 2 },
								marginBottom: { md: 0 },
								textAlign: 'left',
								alignItems: 'center',
							}}
						>
							<Button
								variant='text'
								onClick={(e) => {
									e.stopPropagation();
									onSubscription(policy.policyToken, policy.policyNumber);
								}}
								sx={{
									width: 'auto',
									color: 'info.main',
									textTransform: 'unset',
									':hover': {
										background: 'transparent',
										color: 'info.light',
										textDecoration: 'underline',
									},
								}}
							>
								<Typography sx={{ textAlign: { xs: 'left', sm: 'left', md: 'center' } }}>
									This policy can be restarted, click into it to select a start date
								</Typography>
							</Button>
						</Alert>
					)}
				</Grid>
			</Grid>
		</Paper>
	);
};

export default PolicyListItem;
